import React, { useState } from 'react';
import { careerApi } from '../services/api';
import './CareerDiscovery.css';

const CareerDiscovery = ({ email, userId }) => {
  const [careerGoal, setCareerGoal] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');

  const loadingMessages = [
    'Analyzing your career path...',
    'Identifying required skills...',
    'Calculating investment needs...',
    'Creating your personalized roadmap...',
    'Almost there...'
  ];

  const startLoadingMessages = () => {
    let index = 0;
    setLoadingMessage(loadingMessages[index]);

    const interval = setInterval(() => {
      index = (index + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[index]);
    }, 8000);

    return interval;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!careerGoal.trim()) {
      setError('Please tell us what you\'d like to learn, master, or achieve');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const messageInterval = startLoadingMessages();

    try {
      const roadmap = await careerApi.discoverCareer(careerGoal, userId);
      console.log('Raw API Response:', roadmap);
      console.log('Response type:', typeof roadmap);
      console.log('Response keys:', Object.keys(roadmap || {}));
      setResult(roadmap);
    } catch (err) {
      console.error('Career discovery error:', err);

      if (err.message.includes('timeout') || err.message.includes('took too long')) {
        setError('This is taking longer than expected. Please try again.');
      } else if (err.message.includes('Failed to fetch')) {
        setError('Connection failed. Please check your internet and try again.');
      } else {
        setError(err.message || 'We couldn\'t generate your roadmap. Please try a different career goal or contact support.');
      }
    } finally {
      clearInterval(messageInterval);
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setCareerGoal('');
  };

  const parseWorkflowResponse = (data) => {
    // Parse the workflow response - it should contain combined output from all agents
    // The structure may vary, so we'll handle different formats

    console.log('parseWorkflowResponse received:', data);

    // If data has a specific structure from the workflow
    if (data && typeof data === 'object') {
      // Try to extract information intelligently
      // The workflow returns a "response" field with the actual content
      const content = data.response || data.content || data.result || data.output || JSON.stringify(data);

      console.log('Extracted content:', content);
      console.log('Content type:', typeof content);

      const parsed = {
        rawContent: content,
        title: data.title || 'Your Quest Roadmap',
        summary: data.summary || '',
        skills: data.skills || [],
        investment: data.investment || null,
        timeline: data.timeline || [],
        nextSteps: data.nextSteps || data.recommendations || [],
        resources: data.resources || []
      };

      console.log('Parsed result:', parsed);
      return parsed;
    }

    return {
      rawContent: typeof data === 'string' ? data : JSON.stringify(data, null, 2),
      title: 'Your Quest Roadmap',
      summary: '',
      skills: [],
      investment: null,
      timeline: [],
      nextSteps: [],
      resources: []
    };
  };

  const renderResults = () => {
    if (!result) return null;

    const parsed = parseWorkflowResponse(result);

    console.log('Rendering with parsed data:', parsed);
    console.log('parsed.rawContent:', parsed.rawContent);
    console.log('parsed.rawContent type:', typeof parsed.rawContent);

    return (
      <div className="career-results">
        <div className="results-header">
          <h2>{parsed.title}</h2>
          <button onClick={handleReset} className="btn btn-secondary">
            Generate Another Roadmap
          </button>
        </div>

        {/* Raw content display - since the workflow output structure is not fully defined */}
        <div className="result-section overview-card">
          <h3>Quest Analysis</h3>
          <div className="career-goal-display">
            <strong>Your Quest:</strong> {careerGoal}
          </div>
          {parsed.summary && (
            <p className="summary">{parsed.summary}</p>
          )}
          {parsed.rawContent && typeof parsed.rawContent === 'string' && (
            <div className="workflow-output">
              <pre>{parsed.rawContent}</pre>
            </div>
          )}
          {parsed.rawContent && typeof parsed.rawContent === 'object' && (
            <div className="workflow-output structured">
              {Object.entries(parsed.rawContent).map(([key, value]) => (
                <div key={key} className="output-section">
                  <h4>{key.replace(/([A-Z])/g, ' $1').trim()}</h4>
                  <div className="output-content">
                    {typeof value === 'string' ? (
                      <p>{value}</p>
                    ) : (
                      <pre>{JSON.stringify(value, null, 2)}</pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Skills Roadmap - if available */}
        {parsed.skills && parsed.skills.length > 0 && (
          <div className="result-section skills-roadmap">
            <h3>Skills Roadmap</h3>
            <div className="skills-grid">
              {parsed.skills.map((skill, index) => (
                <div key={index} className="skill-card">
                  <div className="skill-header">
                    <h4>{skill.name || skill.skillName}</h4>
                    <span className={`difficulty-badge ${(skill.level || skill.difficulty || '').toLowerCase()}`}>
                      {skill.level || skill.difficulty || 'Intermediate'}
                    </span>
                  </div>
                  {skill.description && <p className="skill-description">{skill.description}</p>}
                  {skill.duration && (
                    <div className="skill-duration">
                      <span className="icon">⏱️</span> {skill.duration}
                    </div>
                  )}
                  {skill.priority && (
                    <div className="skill-priority">
                      Priority: {skill.priority}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Investment Breakdown - if available */}
        {parsed.investment && (
          <div className="result-section investment-breakdown">
            <h3>Investment Summary</h3>
            <div className="investment-grid">
              {parsed.investment.startupCostMin !== undefined && (
                <div className="investment-item">
                  <div className="investment-label">Startup Cost</div>
                  <div className="investment-value">
                    ${parsed.investment.startupCostMin} - ${parsed.investment.startupCostMax}
                  </div>
                </div>
              )}
              {parsed.investment.monthlyCostMin !== undefined && (
                <div className="investment-item">
                  <div className="investment-label">Monthly Cost</div>
                  <div className="investment-value">
                    ${parsed.investment.monthlyCostMin} - ${parsed.investment.monthlyCostMax}
                  </div>
                </div>
              )}
              {parsed.investment.timePerWeek && (
                <div className="investment-item">
                  <div className="investment-label">Time Commitment</div>
                  <div className="investment-value">{parsed.investment.timePerWeek}</div>
                </div>
              )}
              {parsed.investment.totalMonths && (
                <div className="investment-item">
                  <div className="investment-label">Total Duration</div>
                  <div className="investment-value">{parsed.investment.totalMonths} months</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timeline - if available */}
        {parsed.timeline && parsed.timeline.length > 0 && (
          <div className="result-section timeline-section">
            <h3>Milestone Timeline</h3>
            <div className="timeline">
              {parsed.timeline.map((milestone, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <div className="timeline-period">{milestone.period}</div>
                    <div className="timeline-milestone">{milestone.milestone || milestone.description}</div>
                    {milestone.skills && milestone.skills.length > 0 && (
                      <div className="timeline-skills">
                        {milestone.skills.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next Steps - if available */}
        {parsed.nextSteps && parsed.nextSteps.length > 0 && (
          <div className="result-section next-steps">
            <h3>Next Steps</h3>
            <div className="steps-list">
              {parsed.nextSteps.map((step, index) => (
                <div key={index} className="step-item">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-content">
                    <div className="step-action">{step.action || step.title || step}</div>
                    {step.description && <p className="step-description">{step.description}</p>}
                    {step.timeEstimate && (
                      <div className="step-time">
                        <span className="icon">⏱️</span> {step.timeEstimate}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resources - if available */}
        {parsed.resources && parsed.resources.length > 0 && (
          <div className="result-section resources-section">
            <h3>Recommended Resources</h3>
            <div className="resources-grid">
              {parsed.resources.map((resource, index) => (
                <div key={index} className="resource-card">
                  <div className="resource-type">{resource.type || 'Resource'}</div>
                  <div className="resource-name">{resource.name || resource.title}</div>
                  {resource.cost && <div className="resource-cost">{resource.cost}</div>}
                  {resource.url && (
                    <a href={resource.url} target="_blank" rel="noopener noreferrer" className="resource-link">
                      View Resource →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="career-discovery">
      <div className="career-header">
        <h1>Quest Builder</h1>
        <p className="career-subtitle">
          From career ambitions to personal passion - Tell us what you'd like to learn, master, or achieve - and we'll build your personalized roadmap
        </p>
        {email && <p className="user-email">Your email: {email}</p>}
      </div>

      {!result && (
        <form onSubmit={handleSubmit} className="career-form">
          <div className="form-group">
            <label htmlFor="careerGoal">What's your next challenge?</label>
            <textarea
              id="careerGoal"
              value={careerGoal}
              onChange={(e) => setCareerGoal(e.target.value)}
              placeholder="e.g., I want to become a Data Scientist..."
              rows="4"
              disabled={loading}
              required
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-large"
            disabled={loading || !careerGoal.trim()}
          >
            {loading ? 'Generating Your Roadmap...' : 'Generate my Roadmap'}
          </button>

          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p className="loading-message">{loadingMessage}</p>
            </div>
          )}
        </form>
      )}

      {renderResults()}
    </div>
  );
};

export default CareerDiscovery;
